/**
 * ExtractionCertifyControl — the Data-Specialist / accountant "certify document
 * extraction" gate.
 *
 * GET  /api/v1/extraction-review/{reportId}/certification  (fresh audit + can_certify + blockers)
 * POST /api/v1/extraction-review/{reportId}/certify        (fail-closed; writes audit_chain)
 *
 * FAIL-CLOSED: unknown / in-flight / errored state never certifies. Blocked
 * renders the server's blocker strings VERBATIM. Amber health certifies only
 * with an explicit acknowledge_amber on the POST. Two-step arm→confirm; no modal.
 */
import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../utils/api';
import { Lock, Loader2 } from 'lucide-react';

interface CertAudit {
  overall_health: string | null;
  total_files: number | null;
  extraction_failure_count: number | null;
  merchant_files_without_envelope?: string[];
  unrouted_processor_files?: string[];
  envelope_check_skipped?: boolean;
  audited_at?: string | null;
  audit_warnings?: string[];
}

interface CertifiedBy {
  user_id: string;
  role: string;
  email?: string | null;
  actor_type: string;
}

interface Certification {
  certified_at: string;
  certified_by: CertifiedBy;
  certification_run_id: string;
  notes?: string | null;
}

interface CertResponse {
  report_id: string;
  certification: Certification | null;
  audit: CertAudit;
  can_certify: boolean;
  blockers: string[];
}

interface ExtractionCertifyControlProps {
  reportId: string;
  className?: string;
}

/** The thrown api error is a plain Error with `.status`; for a 412 the message
 *  is the JSON-stringified FastAPI `detail`. Pull a human message out of it. */
function serverMessage(err: any): string {
  const raw = err?.message || 'Certification failed';
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed.message || parsed.detail || raw;
    }
  } catch {
    /* not JSON — use as-is */
  }
  return raw;
}

export default function ExtractionCertifyControl({ reportId, className }: ExtractionCertifyControlProps) {
  const [data, setData] = useState<CertResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [armed, setArmed] = useState<boolean>(false);
  const [certifying, setCertifying] = useState<boolean>(false);
  const [postError, setPostError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearArmedTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const fetchCert = async () => {
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const result = await api.get<CertResponse>(
        `/api/v1/extraction-review/${reportId}/certification`,
        { showErrorToast: false }
      );
      setData(result);
    } catch (err: any) {
      const status = err?.status;
      if (status === 403) {
        setForbidden(true);
      } else {
        setError(err?.message || 'Failed to load certification status');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (reportId) fetchCert();
    return () => clearArmedTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  useEffect(() => {
    if (armed) {
      clearArmedTimer();
      timerRef.current = setTimeout(() => setArmed(false), 6000);
    } else {
      clearArmedTimer();
    }
    return () => clearArmedTimer();
  }, [armed]);

  const handleButtonClick = async () => {
    if (!data || certifying) return;
    if (!armed) {
      setArmed(true);
      setPostError(null);
      return;
    }

    setCertifying(true);
    setPostError(null);
    try {
      const body = {
        acknowledge_amber: data.audit.overall_health === 'amber',
        recertify: false,
        notes: null as string | null,
      };
      const res = await api.post<any>(
        `/api/v1/extraction-review/${reportId}/certify`,
        body,
        { showErrorToast: false }
      );
      if (res?.certification) {
        setData(prev => (prev ? { ...prev, certification: res.certification } : prev));
      } else {
        // Unexpected success shape — refetch so the seal/blocked state is truthful.
        fetchCert();
      }
    } catch (err: any) {
      setPostError(`CERTIFICATION FAILED — ${serverMessage(err)}`);
      fetchCert();
    } finally {
      setCertifying(false);
      setArmed(false);
    }
  };

  if (forbidden) return null;

  const isBlocked = !!data && !data.certification && !data.can_certify;
  const isCertifiable = !!data && !data.certification && data.can_certify;
  const isSealed = !!data && !!data.certification;
  const disabled = loading || !data || !!error || isBlocked || certifying;

  return (
    <div className={className}>
      <div className="flex items-center justify-end">
        {isSealed ? (
          <div
            className="inline-flex flex-col px-4 py-2 border rounded-[2px]"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <span className="font-mono text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-primary)' }}>
              ✓ Certified
            </span>
            <span className="font-mono text-[11px]" style={{ color: 'var(--color-text-secondary)' }}>
              {data!.certification!.certified_by.email || data!.certification!.certified_by.role} · {data!.certification!.certified_at}
            </span>
          </div>
        ) : (
          <button
            onClick={handleButtonClick}
            disabled={disabled}
            onBlur={() => setArmed(false)}
            aria-label="Certify document extraction"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-[2px] text-xs uppercase tracking-wider font-mono transition-colors ${
              disabled
                ? 'border cursor-not-allowed'
                : armed
                ? 'bg-[var(--color-brand-hover)] text-white'
                : 'bg-[var(--color-brand)] text-white hover:bg-[var(--color-brand-hover)]'
            }`}
            style={disabled ? { borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' } : undefined}
          >
            {(loading || !data) && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {certifying && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {isBlocked && !certifying && <Lock className="w-3.5 h-3.5" />}
            {loading || !data
              ? 'Checking…'
              : error
              ? 'Unavailable'
              : certifying
              ? 'Certifying…'
              : armed
              ? 'Confirm Certify'
              : 'Certify Extraction'}
          </button>
        )}
      </div>

      {error && !data && (
        <div className="mt-2 text-right">
          <span className="text-xs font-mono" style={{ color: 'var(--color-blocker)' }}>{error}</span>
        </div>
      )}

      {postError && (
        <div className="mt-2 text-right">
          <span className="text-xs font-mono" style={{ color: 'var(--color-blocker)' }}>{postError}</span>
        </div>
      )}

      {isBlocked && (
        <div
          className="mt-3 border-t border-b"
          style={{ backgroundColor: 'var(--color-blocker-tint)', borderColor: 'var(--color-blocker-tint-bd)' }}
        >
          <div className="px-4 py-3 max-h-[120px] overflow-y-auto">
            <div className="font-mono text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--color-blocker)' }}>
              Certification Blocked — {data!.blockers.length} Blocker{data!.blockers.length === 1 ? '' : 's'}
            </div>
            <ul className="space-y-1">
              {data!.blockers.map((blocker, idx) => (
                <li key={idx} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--color-text-primary)' }}>
                  <span className="flex-shrink-0 font-semibold" style={{ color: 'var(--color-blocker)' }} aria-hidden>✕</span>
                  <span>{blocker}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {isCertifiable && data!.audit.overall_health === 'amber' && (
        <div className="mt-2 text-right">
          <span className="font-mono text-[11px]" style={{ color: 'var(--color-text-secondary)' }}>
            Health: Amber — Review Advised
          </span>
        </div>
      )}
    </div>
  );
}
